// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure geometry for the two hand-rolled layouts on this site. No DOM, no
// randomness, no animation — callers run these to completion and render the
// result exactly once.
//
// These live in their own module so the positional tests can assert what
// actually matters: no overlap, inside the canvas, no NaN. Area- or count-only
// tests pass happily on a layout that stacks every mark at one point.

export interface Bin {
  /** Inclusive lower edge and exclusive upper edge, in data units. */
  lo: number;
  hi: number;
  count: number;
  /** Indices of the values that landed in this bin. */
  items: number[];
}

/**
 * Fixed-count bins over a log-spaced domain. Used instead of a beeswarm for the
 * co-funded distance distribution: 2,161 towers are so concentrated around the
 * mode that a swarm has to shrink its dots to a hairline to avoid overlapping,
 * which reads as a smear and hides the very shape it exists to show.
 *
 * Values at or below `min` fall in the first bin and values at or above `max`
 * in the last, so nothing is silently dropped off either end.
 */
export function logBins(values: number[], min: number, max: number, count: number): Bin[] {
  const lo = Math.log10(min);
  const hi = Math.log10(max);
  const step = (hi - lo) / count;
  const bins: Bin[] = Array.from({ length: count }, (_, i) => ({
    lo: 10 ** (lo + i * step),
    hi: 10 ** (lo + (i + 1) * step),
    count: 0,
    items: [],
  }));
  values.forEach((v, i) => {
    if (!Number.isFinite(v)) return;
    const clamped = Math.min(Math.max(v, min), max);
    let idx = Math.floor((Math.log10(clamped) - lo) / step);
    if (idx < 0) idx = 0;
    if (idx >= count) idx = count - 1;
    bins[idx].count++;
    bins[idx].items.push(i);
  });
  return bins;
}

export interface Box {
  y: number;
  h: number;
}

/**
 * Stack one alluvial column: a box per state, in a fixed order, heights
 * proportional to counts, separated by `gap`. The fixed order is what stops a
 * state drifting between years, which is what makes the ribbons readable.
 */
export function stackColumn(
  counts: number[],
  opts: { top: number; scale: (v: number) => number; gap: number },
): Box[] {
  let y = opts.top;
  return counts.map((c) => {
    const h = Math.max(0, opts.scale(c));
    const box = { y, h };
    y += h + opts.gap;
    return box;
  });
}

export interface Slice {
  /** Offset from the top of the node box where this ribbon attaches. */
  offset: number;
  h: number;
}

/**
 * Allocate ribbon attachment points down the side of a node. Slices are laid
 * end to end in the order given, so together they exactly fill the node's
 * height and never overlap.
 */
export function allocate(counts: number[], scale: (v: number) => number): Slice[] {
  let offset = 0;
  return counts.map((c) => {
    const h = Math.max(0, scale(c));
    const slice = { offset, h };
    offset += h;
    return slice;
  });
}

/** Cubic Bézier outline of a ribbon between two vertical edges. */
export function ribbonPath(x0: number, y0: number, x1: number, y1: number, h: number): string {
  const cx = (x0 + x1) / 2;
  const f = (n: number) => n.toFixed(1);
  return (
    `M${f(x0)},${f(y0)} ` +
    `C${f(cx)},${f(y0)} ${f(cx)},${f(y1)} ${f(x1)},${f(y1)} ` +
    `L${f(x1)},${f(y1 + h)} ` +
    `C${f(cx)},${f(y1 + h)} ${f(cx)},${f(y0 + h)} ${f(x0)},${f(y0 + h)} Z`
  );
}

/** Log-scale position mapper, clamped to the domain. */
export function logScale(min: number, max: number, from: number, to: number): (v: number) => number {
  const lo = Math.log10(min);
  const hi = Math.log10(max);
  return (v: number) => {
    const c = Math.min(Math.max(v, min), max);
    return from + ((Math.log10(c) - lo) / (hi - lo)) * (to - from);
  };
}
